import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation, type Lang } from "@/i18n/useTranslation";

const sectionIds = ["services", "about", "contact"] as const;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t, lang, setLang } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const navLabels: Record<typeof sectionIds[number], string> = {
    services: t.nav.services,
    about: t.nav.about,
    contact: t.nav.contact,
  };

  const scrollTo = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        if (id === "hero") window.scrollTo({ top: 0 });
        else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleLang = () => setLang(lang === "en" ? "da" : "en");

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/60">
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <button
          onClick={() => scrollTo("hero")}
          className="font-display text-xl font-bold text-foreground tracking-tight"
        >
          East<span className="text-gradient">West</span>IT
        </button>

        <div className="hidden md:flex items-center gap-8">
          {sectionIds.map((s) => (
            <button
              key={s}
              onClick={() => scrollTo(s)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              style={{ transitionDuration: "var(--duration-fast)" }}
            >
              {navLabels[s]}
            </button>
          ))}
          <button
            onClick={toggleLang}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-border rounded-md px-2.5 py-1 transition-colors uppercase tracking-wider"
          >
            {lang === "en" ? "DA" : "EN"}
          </button>
          <Button size="sm" variant="premium" onClick={() => scrollTo("contact")}>
            {t.nav.cta}
          </Button>
        </div>

        <button
          className="md:hidden text-foreground"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-b border-border/60 px-4 pb-4 flex flex-col gap-3 animate-fade-up">
          {sectionIds.map((s) => (
            <button
              key={s}
              onClick={() => scrollTo(s)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors text-left py-2"
            >
              {navLabels[s]}
            </button>
          ))}
          <button
            onClick={toggleLang}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-border rounded-md px-2.5 py-1 transition-colors uppercase tracking-wider self-start"
          >
            {lang === "en" ? "DA" : "EN"}
          </button>
          <Button size="sm" variant="premium" onClick={() => scrollTo("contact")}>
            {t.nav.cta}
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
